<script>
export default {
  name: 'VimeoPlayerOptimized',
  emits: ['paused', 'muted', 'timeupdate', 'progress', 'ready'],
  props: {
    value: { type: Object, required: true },
    autoload: { type: Boolean, default: true },
    showControls: { type: Boolean, default: true }
  },

  watch: {
    'value.vimeoId' (newId, oldId) {
      if (newId !== oldId && oldId !== undefined) {
        this.resetPlayer()
      }
    },
    videoReady (newVal) {
      if (newVal) {
        this.$emit('ready')
      }
    }
  },

  data: () => ({
    videoLoaded: false,
    videoReady: false,
    videoCanPlay: false,
    thumbnailLoaded: false,
    thumbnailUrl: null,
    player: null,
    isPlaying: false,
    isMuted: true,
    currentTime: 0,
    duration: 0,
    timeUpdateInterval: null
  }),

  computed: {
    videoDimensionsStyle () {
      return null
    },

    vimeoEmbedUrl () {
      if (!this.value.vimeoId) return ''

      // quality=auto starts lower then ramps up — faster first frame than forcing 1080p
      const params = new URLSearchParams({
        background: '1',
        autoplay: '1',
        loop: '1',
        byline: '0',
        portrait: '0',
        title: '0',
        muted: '1',
        controls: '0',
        transparent: '0',
        quality: 'auto',
        dnt: '1'
      })

      return `https://player.vimeo.com/video/${this.value.vimeoId}?${params.toString()}`
    }
  },

  mounted () {
    this.loadThumbnail()
    if (this.autoload) {
      this.loadVideo()
    }
    this.loadVimeoAPI()
  },

  beforeDestroy () {
    this.cleanup()
  },

  methods: {
    async loadThumbnail () {
      if (this.value && this.value.url) {
        this.thumbnailUrl = this.value.url
        return
      }

      try {
        const response = await fetch(`https://vimeo.com/api/v2/video/${this.value.vimeoId}.json`)
        const data = await response.json()
        if (data && data[0] && data[0].thumbnail_large) {
          this.thumbnailUrl = data[0].thumbnail_large
        }
      } catch (error) {
        console.warn('Failed to load Vimeo thumbnail:', error)
      }
    },

    loadVideo () {
      this.videoLoaded = true
    },

    loadVimeoAPI () {
      if (window.Vimeo) return Promise.resolve()

      return new Promise((resolve) => {
        const existing = document.querySelector('script[data-vimeo-api]')
        if (existing) {
          existing.addEventListener('load', () => resolve())
          return
        }
        const script = document.createElement('script')
        script.src = 'https://player.vimeo.com/api/player.js'
        script.async = true
        script.dataset.vimeoApi = 'true'
        script.onload = () => resolve()
        document.head.appendChild(script)
      })
    },

    initializeVimeoAPI () {
      if (!this.$refs.vimeoIframe || !window.Vimeo || this.player) return

      try {
        this.player = new window.Vimeo.Player(this.$refs.vimeoIframe)

        this.player.on('pause', () => {
          this.isPlaying = false
          this.$emit('paused', true)
          this.stopTimeUpdates()
        })

        this.player.on('timeupdate', (data) => {
          this.videoReady = true
          this.currentTime = data.seconds
          this.$emit('timeupdate', {
            seconds: data.seconds,
            duration: this.duration
          })
        })

        this.player.on('progress', (data) => {
          this.$emit('progress', data)
        })

        this.player.on('loadeddata', () => {
          this.videoReady = true
        })

        this.player.on('play', () => {
          this.isPlaying = true
          this.videoReady = true
          this.$emit('paused', false)
          this.startTimeUpdates()
        })

        this.player.on('error', (error) => {
          console.error('Vimeo player error:', error)
        })

        this.player.getDuration().then(duration => {
          this.duration = duration
        }).catch(err => {
          console.warn('Failed to get duration:', err)
        })

        this.player.ready().then(async () => {
          this.videoReady = true
          try {
            let paused = await this.player.getPaused()
            if (paused) {
              try {
                await this.player.play()
                paused = await this.player.getPaused()
              } catch (playError) {
                console.warn('Force play failed (probably requires user interaction):', playError)
              }
            }
            this.isPlaying = !paused
            this.$emit('paused', paused)
            if (!paused) {
              this.startTimeUpdates()
              this.videoCanPlay = true
            }
          } catch (err) {
            console.warn('Failed to get paused state:', err)
          }
          this.player.getCurrentTime().then(time => {
            this.currentTime = time
            this.$emit('timeupdate', {
              seconds: time,
              duration: this.duration
            })
          }).catch(err => {
            console.warn('Failed to get current time:', err)
          })
        })
      } catch (error) {
        console.error('Failed to initialize Vimeo player:', error)
      }
    },

    onVideoLoad () {
      this.videoReady = true
      this.$nextTick(() => {
        if (window.Vimeo) {
          this.initializeVimeoAPI()
        } else {
          this.loadVimeoAPI().then(() => this.initializeVimeoAPI())
        }
      })
    },

    async togglePlay (value) {
      if (!this.videoLoaded) {
        this.loadVideo()
        await new Promise(resolve => setTimeout(resolve, 200))
      }
      let attempts = 0
      while (!this.player && attempts < 10) {
        await new Promise(resolve => setTimeout(resolve, 100))
        attempts++
      }
      if (!this.player) {
        console.warn('Player not ready after waiting')
        this.isPlaying = typeof value !== 'undefined' ? !value : !this.isPlaying
        this.$emit('paused', !this.isPlaying)
        return
      }

      try {
        const currentPaused = await this.player.getPaused()
        let targetPaused
        if (typeof value !== 'undefined') {
          targetPaused = value
        } else {
          targetPaused = !currentPaused
        }
        if (targetPaused !== currentPaused) {
          if (targetPaused) {
            await this.player.pause()
          } else {
            await this.player.play()
          }
        }
        await new Promise(resolve => setTimeout(resolve, 150))
        const finalPaused = await this.player.getPaused()
        this.isPlaying = !finalPaused
        this.$emit('paused', finalPaused)
        const currentTime = await this.player.getCurrentTime()
        this.currentTime = currentTime
        this.$emit('timeupdate', {
          seconds: currentTime,
          duration: this.duration
        })
      } catch (error) {
        console.warn('Failed to toggle play:', error)
      }
    },

    async toggleSound (value) {
      if (!this.videoLoaded) {
        this.loadVideo()
        await new Promise(resolve => setTimeout(resolve, 100))
      }
      if (!this.player) {
        this.isMuted = typeof value !== 'undefined' ? value : !this.isMuted
        this.$emit('muted', this.isMuted)
        return
      }
      try {
        let muted
        if (typeof value !== 'undefined') {
          muted = value
        } else {
          muted = await this.player.getMuted()
        }
        if (muted) {
          await this.player.setMuted(false)
          await this.player.setVolume(1)
        } else {
          await this.player.setMuted(true)
        }
        muted = await this.player.getMuted()
        this.isMuted = muted
        this.$emit('muted', muted)
      } catch (error) {
        console.warn('Failed to toggle sound:', error)
      }
    },

    play () {
      return this.togglePlay(false)
    },

    pause () {
      return this.togglePlay(true)
    },

    mute () {
      return this.toggleSound(true)
    },

    unmute () {
      return this.toggleSound(false)
    },

    startTimeUpdates () {
      this.stopTimeUpdates()
      this.timeUpdateInterval = setInterval(async () => {
        if (this.player && this.isPlaying) {
          try {
            const currentTime = await this.player.getCurrentTime()
            this.currentTime = currentTime
            this.$emit('timeupdate', {
              seconds: currentTime,
              duration: this.duration
            })
          } catch (error) {
            console.warn('Failed to get current time:', error)
          }
        }
      }, 250)
    },

    stopTimeUpdates () {
      if (this.timeUpdateInterval) {
        clearInterval(this.timeUpdateInterval)
        this.timeUpdateInterval = null
      }
    },

    cleanup () {
      this.stopTimeUpdates()
      if (this.player) {
        try {
          this.player.off('play')
          this.player.off('pause')
          this.player.off('timeupdate')
          this.player.off('progress')
          this.player.off('loaded')
          this.player.off('bufferend')
          this.player.off('error')
          this.player = null
        } catch (error) {
          console.warn('Error cleaning up player:', error)
        }
      }
    },

    resetPlayer () {
      this.cleanup()
      this.videoLoaded = false
      this.videoReady = false
      this.videoCanPlay = false
      this.thumbnailLoaded = false
      this.thumbnailUrl = null
      this.player = null
      this.isPlaying = false
      this.isMuted = true
      this.currentTime = 0
      this.duration = 0
      this.$emit('paused', true)
      this.$emit('muted', true)
      this.$emit('timeupdate', { seconds: 0, duration: 0 })
      this.$nextTick(() => {
        this.loadThumbnail()
        if (this.autoload) {
          this.loadVideo()
        }
      })
    }
  }
}
</script>

<template>
  <div ref="box" class="vimeo-player-box relative">
    <iframe v-if="videoLoaded" ref="vimeoIframe" :src="vimeoEmbedUrl" class="video-player"
      :style="videoDimensionsStyle" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen
      @load="onVideoLoad">
    </iframe>
    <div
      class="video-thumbnail"
      :class="{ 'is-hidden': videoReady }"
      @click="!videoLoaded && loadVideo()">
      <img
        v-if="thumbnailUrl"
        :src="thumbnailUrl"
        :alt="`Video thumbnail for ${value.vimeoId}`"
        class="w-full h-full object-cover"
        @load="thumbnailLoaded = true"
        @error="thumbnailLoaded = true">
    </div>
    <div v-if="videoLoaded && showControls" class="video-hitarea" @click="togglePlay">
    </div>
  </div>
</template>

<style lang="scss">
.vimeo-player-box {
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: calc(100% / (var(--ar, 1.7777777778)));
  overflow: hidden;
  background: #111;

  .video-player,
  iframe {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }

  .video-thumbnail {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1;
    opacity: 1;
    transition: opacity 0.8s ease-in-out;
    pointer-events: auto;

    &.is-hidden {
      opacity: 0;
      pointer-events: none;
    }
  }

  .video-hitarea {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 2;
    cursor: pointer;
  }
}

.vimeo-player-box.player-aspect-ratio {
  .video-player {
    position: absolute;
    top: 0;
    left: 0;
  }
}
</style>
